import type * as Sentry from '@sentry/react';

// Network error patterns to ignore
const IGNORED_NETWORK_ERRORS = [
  'Failed to fetch',
  'NetworkError',
  'Network request failed',
  'net::ERR_',
  'NS_ERROR_',
  'Load failed',
  'The Internet connection appears to be offline',
  'timeout of',
  'Request aborted',
  'Request timed out',
];

// Auth error patterns to ignore
const IGNORED_AUTH_ERRORS = [
  'Firebase: Error (auth/popup-closed-by-user)',
  'Firebase: Error (auth/cancelled-popup-request)',
  'Firebase: Error (auth/network-request-failed)',
  'The user is not authenticated',
];

// GraphQL error codes to ignore
const IGNORED_GRAPHQL_CODES = [
  'GRAPHQL_VALIDATION_FAILED',
  'permission-denied',
  'access-denied',
  'jwt-expired',
  'jwt-invalid-claims',
];

// Browser/extension errors to ignore
const IGNORED_BROWSER_ERRORS = [
  'ResizeObserver loop',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'AutocompleteService',
  'google.maps.places',
  'Google Maps already loaded',
  'Google Maps JavaScript API could not load',
  'InvalidValueError',
  'Connection to Indexed Database server lost',
  'IndexedDB',
  'IDBDatabase',
  'IDBTransaction',
  'UnknownError: Connection to Indexed Database',
  'Could not load "util"',
  'Error invoking postEvent',
  'postEvent: Method not found',
];

// Bot user agents to ignore
const BOT_USER_AGENTS = [
  'bot',
  'crawler',
  'spider',
  'scraper',
  'facebookexternalhit',
  'twitterbot',
  'linkedinbot',
  'whatsapp',
];

function isIgnoredNetworkError(error: Error | string): boolean {
  const errorMessage = typeof error === 'string' ? error : error.message;
  return IGNORED_NETWORK_ERRORS.some((pattern) =>
    errorMessage.toLowerCase().includes(pattern.toLowerCase())
  );
}

function isIgnoredAuthError(error: Error | string): boolean {
  const errorMessage = typeof error === 'string' ? error : error.message;
  return IGNORED_AUTH_ERRORS.some((pattern) => errorMessage.includes(pattern));
}

function isIgnoredGraphQLError(hint: Sentry.EventHint): boolean {
  const originalException = hint.originalException;

  if (!originalException || typeof originalException !== 'object') {
    return false;
  }
  if ('response' in originalException) {
    const response = originalException.response as {
      errors?: Array<{ extensions?: { code?: string } }>;
      status?: number;
    };

    if (response.status === 401 || response.status === 403) {
      return true;
    }

    if (response.errors) {
      return response.errors.some((err) => {
        const code = err.extensions?.code;
        return (
          code &&
          IGNORED_GRAPHQL_CODES.some((ignored) =>
            code.toLowerCase().includes(ignored.toLowerCase())
          )
        );
      });
    }
  }

  return false;
}

function isIgnoredBrowserError(error: Error | string): boolean {
  const errorMessage = typeof error === 'string' ? error : error.message;
  return IGNORED_BROWSER_ERRORS.some((pattern) => errorMessage.includes(pattern));
}

function isBot(userAgent?: string): boolean {
  if (!userAgent) return false;
  const ua = userAgent.toLowerCase();
  return BOT_USER_AGENTS.some((bot) => ua.includes(bot));
}

function shouldCaptureByStatus(hint: Sentry.EventHint): boolean {
  const originalException = hint.originalException;

  if (!originalException || typeof originalException !== 'object') {
    return true;
  }
  if ('status' in originalException) {
    const status = originalException.status as number;
    if (status >= 400 && status < 500) {
      return false;
    }
  }

  if ('response' in originalException) {
    const response = originalException.response as { status?: number };
    if (response.status && response.status >= 400 && response.status < 500) {
      return false;
    }
  }

  return true;
}

export function beforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  if (isBot(event.request?.headers?.['User-Agent'])) {
    return null;
  }

  const error = hint.originalException;

  if (error && isIgnoredNetworkError(error as Error)) {
    return null;
  }

  if (error && isIgnoredAuthError(error as Error)) {
    return null;
  }

  if (isIgnoredGraphQLError(hint)) {
    return null;
  }

  if (error && isIgnoredBrowserError(error as Error)) {
    return null;
  }

  if (!shouldCaptureByStatus(hint)) {
    return null;
  }

  if (error instanceof Error && error.name === 'AbortError') {
    return null;
  }

  if (event.request?.headers) {
    delete event.request.headers.Authorization;
    delete event.request.headers['X-Hasura-Role'];
    delete event.request.headers['x-hasura-user-id'];
  }

  if (event.exception?.values?.[0]) {
    const exception = event.exception.values[0];
    if (exception.type && exception.value) {
      event.fingerprint = [exception.type, exception.value];
    }
  }

  return event;
}

export function getSentryConfig(): Sentry.BrowserOptions | null {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const enabled = import.meta.env.VITE_SENTRY_ENABLED === 'true';
  const environment = import.meta.env.MODE || 'development';

  if (!enabled || !dsn) {
    console.info('[Sentry] Disabled - no DSN or not enabled');
    return null;
  }

  const tracesSampleRate =
    Number.parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string) || 0.1;

  const replaysSessionSampleRate =
    Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE as string) || 0.01;

  const replaysOnErrorSampleRate =
    Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE as string) || 1.0;

  const buildVersion = import.meta.env.VITE_BUILD_VERSION as string | undefined;

  return {
    dsn,
    environment,
    release: buildVersion ? `dtk-admin@${buildVersion}` : undefined,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    beforeSend,
    sendDefaultPii: false,
    maxBreadcrumbs: 50,
    integrations: [],
    normalizeDepth: 10,
    maxValueLength: 1000,
    ignoreErrors: [
      'top.GLOBALS',
      'atomicFindClose',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'chrome-extension',
      'moz-extension',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Connection to Indexed Database server lost',
      'IndexedDB',
      'IDBDatabase',
      'IDBTransaction',
    ],
    ignoreTransactions: ['/version.json', '/manifest.json', '/robots.txt', '/favicon.ico'],
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/hasura\.zaslogistica\.com/,
      /^https:\/\/hasura-stage\.zaslogistica\.com/,
      /^https:\/\/geo-api.*\.site/,
    ],
  };
}
